/**
 * Shared website + social normalization for candidate directory and reports.
 */

import { SocialMediaLink, SocialPlatform } from './types';

export type RawSocialRow = {
  platform?: string | null;
  handle?: string | null;
  url?: string | null;
  contact_value?: string | null;
};

export function ensureHttpsUrl(url: string): string {
  const t = url.trim();
  if (!t) return '';
  return /^https?:\/\//iu.test(t) ? t : `https://${t}`;
}

export function socialProfileUrl(platform: SocialPlatform, handle: string): string {
  const segment = encodeURIComponent(handle.replace(/^@/, ''));
  switch (platform) {
    case 'x':
      return `https://x.com/${segment}`;
    case 'instagram':
      return `https://www.instagram.com/${segment}/`;
    case 'facebook':
      return `https://www.facebook.com/${segment}`;
    case 'linkedin':
      return `https://www.linkedin.com/in/${segment}`;
    case 'bluesky':
      return `https://bsky.app/profile/${segment}`;
    case 'youtube':
      return `https://www.youtube.com/@${segment}`;
    default:
      return '';
  }
}

export function normalizePlatform(p?: string | null): SocialPlatform | null {
  if (!p) return null;
  const lower = p.toLowerCase();
  if (lower === 'x' || lower === 'twitter' || lower.includes('tweet')) return 'x';
  if (lower === 'instagram' || lower === 'ig') return 'instagram';
  if (lower === 'facebook' || lower === 'fb') return 'facebook';
  if (lower === 'linkedin' || lower === 'li') return 'linkedin';
  if (lower.includes('bluesky') || lower === 'bsky') return 'bluesky';
  if (lower.includes('youtube') || lower === 'yt') return 'youtube';
  return null;
}

export function resolveSocialUrl(platform: SocialPlatform, handle: string, storedUrl?: string | null): string {
  const raw = (storedUrl || '').trim();
  if (raw && (/^https?:\/\//iu.test(raw) || raw.includes('.'))) {
    return ensureHttpsUrl(raw);
  }
  return socialProfileUrl(platform, handle);
}

export function normalizeSocialMedia(rows?: RawSocialRow[] | null): SocialMediaLink[] {
  if (!rows || !Array.isArray(rows)) return [];
  const links: SocialMediaLink[] = [];
  for (const row of rows) {
    const platform = normalizePlatform(row.platform);
    const handle = (row.handle || '').replace(/^@/, '').trim();
    if (!platform) continue;
    const url = resolveSocialUrl(platform, handle, row.url || row.contact_value);
    if (!url && !handle) continue;
    links.push({
      platform,
      handle: handle || platform,
      url: url || socialProfileUrl(platform, handle),
    });
  }
  return links;
}

export function mapCandidateContactFields(raw: {
  website_url?: string | null;
  website?: string | null;
  social_media?: RawSocialRow[] | null;
}): { website: string | null; socialMedia: SocialMediaLink[] } {
  const websiteRaw = raw.website_url || raw.website || null;
  const website = websiteRaw ? ensureHttpsUrl(websiteRaw) : null;
  return {
    website,
    socialMedia: normalizeSocialMedia(raw.social_media),
  };
}
